/**
 * Wire-format helpers for the calendar `Date` (dateTime) edit tag.
 *
 * MUST stay byte-for-byte compatible with the backend's `WhenPayload` in
 * `apps/http-gateway/src/lib/toolsets/shared/calendar-edit-options.ts`.
 *
 * The payload the picker seeds from / serializes to:
 *
 *   { start, end, allDay, timeZone? }
 *
 * - Timed (`allDay: false`): `start`/`end` are RFC3339 *with offset*. `timeZone`
 *   is the event's IANA zone, carried through opaquely so the round-trip never
 *   drops it (we don't build a tz selector — see the deferred fast-follow).
 * - All-day (`allDay: true`): `start`/`end` are `YYYY-MM-DD`. `end` is
 *   EXCLUSIVE (Google convention): a single-day Jun 2 event is
 *   start=2026-06-02, end=2026-06-03. `timeZone` is omitted.
 *
 * The picker UI works on local JS `Date`s. For timed events we treat each end's
 * wall-clock (the Y-M-D H:M from the RFC3339 string, IGNORING its offset) as a
 * naive local `Date`, anchored to the event's `timeZone`. On serialize we render
 * that wall-clock at the event zone's offset for that date (DST-aware), so
 * changing the timezone keeps the wall-clock the user sees and re-anchors the
 * instant to the new zone. When no `timeZone` is carried we fall back to the
 * browser's local offset. For all-day events we work on the wall-clock date and
 * apply the exclusive-end (+1 day) convention only at the wire boundary, so the
 * UI shows the human "last day".
 */

export interface WhenPayload {
  start: string
  end: string
  allDay: boolean
  timeZone?: string
}

/** Parse a stored payload string; returns null on any malformed input. */
export function parseWhenPayload(raw: string | undefined): WhenPayload | null {
  if (!raw) return null
  try {
    const p = JSON.parse(raw) as Partial<WhenPayload>
    if (typeof p.start !== 'string' || typeof p.end !== 'string') return null
    return { start: p.start, end: p.end, allDay: !!p.allDay, timeZone: p.timeZone }
  } catch {
    return null
  }
}

/** Format a UTC-minute offset as an RFC3339 suffix, e.g. -420 → "-07:00". */
function formatOffset(offsetMin: number): string {
  const p2 = (n: number) => String(n).padStart(2, '0')
  const sign = offsetMin >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMin)
  return `${sign}${p2(Math.floor(abs / 60))}:${p2(abs % 60)}`
}

/** RFC3339 string at the BROWSER's local offset for a JS Date, e.g. 2026-06-02T09:00:00-07:00. */
export function toLocalRfc3339(date: Date): string {
  const p2 = (n: number) => String(n).padStart(2, '0')
  const body =
    `${date.getFullYear()}-${p2(date.getMonth() + 1)}-${p2(date.getDate())}` +
    `T${p2(date.getHours())}:${p2(date.getMinutes())}:${p2(date.getSeconds())}`
  return `${body}${formatOffset(-date.getTimezoneOffset())}` // JS returns minutes behind UTC
}

/**
 * Parse the WALL-CLOCK of a timed RFC3339 string into a naive local `Date`,
 * IGNORING the offset. The picker anchors a timed event's wall-clock to its own
 * `timeZone`, so we carry the Y-M-D H:M:S as local fields and read/edit them
 * directly. Returns null on malformed input.
 */
export function parseTimedWallClock(value: string): Date | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/)
  if (!m) return null
  return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], m[6] ? +m[6] : 0)
}

/** Offset (minutes east of UTC) of an IANA zone at an absolute instant. */
function offsetAtInstant(timeZone: string, instant: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const parts = Object.fromEntries(dtf.formatToParts(instant).map((p) => [p.type, p.value]))
  const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second)
  return Math.round((asUTC - instant.getTime()) / 60000)
}

/**
 * Offset (minutes east of UTC, e.g. -420 for PDT) of an IANA `timeZone` at a
 * given WALL-CLOCK (a local `Date` whose fields are the intended civil time).
 * Two-pass so a wall-clock near a DST transition settles to the offset in effect.
 */
export function zoneOffsetMinutes(timeZone: string, wallClock: Date): number {
  const asUTC = Date.UTC(
    wallClock.getFullYear(),
    wallClock.getMonth(),
    wallClock.getDate(),
    wallClock.getHours(),
    wallClock.getMinutes(),
    wallClock.getSeconds(),
  )
  const guess = offsetAtInstant(timeZone, new Date(asUTC))
  return offsetAtInstant(timeZone, new Date(asUTC - guess * 60000))
}

/**
 * RFC3339 for a naive wall-clock `Date` rendered at `timeZone`'s offset for that
 * date, e.g. 2026-06-02T09:00:00-07:00 for America/Los_Angeles in summer.
 */
export function wallClockToZonedRfc3339(date: Date, timeZone: string): string {
  const p2 = (n: number) => String(n).padStart(2, '0')
  const body =
    `${date.getFullYear()}-${p2(date.getMonth() + 1)}-${p2(date.getDate())}` +
    `T${p2(date.getHours())}:${p2(date.getMinutes())}:${p2(date.getSeconds())}`
  return `${body}${formatOffset(zoneOffsetMinutes(timeZone, date))}`
}

/**
 * The absolute instant of a naive wall-clock `Date` interpreted in `timeZone`
 * (inverse of reading a zoned RFC3339 as wall-clock). For no zone — or the
 * browser's own zone — this equals the Date's own instant, so local-zone events
 * are unaffected; it only shifts when the event is anchored to a different zone.
 */
export function zonedWallClockToInstant(wallClock: Date, timeZone: string | undefined): Date {
  if (!timeZone) return wallClock
  const offsetMin = zoneOffsetMinutes(timeZone, wallClock)
  const utcMs = Date.UTC(
    wallClock.getFullYear(),
    wallClock.getMonth(),
    wallClock.getDate(),
    wallClock.getHours(),
    wallClock.getMinutes(),
    wallClock.getSeconds(),
  )
  return new Date(utcMs - offsetMin * 60000)
}

/**
 * A naive local `Date` whose fields are the wall-clock of `instant` in `timeZone`
 * (inverse of `zonedWallClockToInstant`). Lets us re-project true-instant calendar
 * events into the same wall-clock frame the picker edits in. No-op without a zone.
 */
export function instantToZonedWallClock(instant: Date, timeZone: string | undefined): Date {
  if (!timeZone) return instant
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const p = Object.fromEntries(dtf.formatToParts(instant).map((x) => [x.type, x.value]))
  return new Date(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second)
}

/** YYYY-MM-DD (local wall-clock) for a JS Date. */
export function toDateString(date: Date): string {
  const p2 = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${p2(date.getMonth() + 1)}-${p2(date.getDate())}`
}

/** Parse YYYY-MM-DD into a local-midnight JS Date (null on malformed input). */
export function parseDateString(value: string): Date | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  return new Date(+m[1], +m[2] - 1, +m[3])
}

/** Add whole days to a YYYY-MM-DD string. */
export function addDays(dateString: string, days: number): string {
  const d = parseDateString(dateString)
  if (!d) return dateString
  d.setDate(d.getDate() + days)
  return toDateString(d)
}

/**
 * The editing model used inside the component: JS Dates for both ends, plus the
 * all-day flag and the carried (opaque) timeZone. For all-day, `endDate` is the
 * INCLUSIVE last day the user sees (the wire +1-day exclusivity is applied only
 * at serialize time).
 */
export interface WhenDraft {
  startDate: Date
  endDate: Date
  allDay: boolean
  timeZone?: string
}

/** Seed an editing draft from a wire payload. */
export function payloadToDraft(payload: WhenPayload): WhenDraft | null {
  if (payload.allDay) {
    const start = parseDateString(payload.start)
    const exclusiveEnd = parseDateString(payload.end)
    if (!start) return null
    // Convert exclusive wire end → inclusive display end (end - 1 day),
    // clamped to never precede start.
    const inclusiveEndStr = exclusiveEnd ? addDays(payload.end, -1) : payload.start
    const inclusiveEnd = parseDateString(inclusiveEndStr) ?? start
    return {
      startDate: start,
      endDate: inclusiveEnd < start ? start : inclusiveEnd,
      allDay: true,
      timeZone: payload.timeZone,
    }
  }
  const start = parseTimedWallClock(payload.start)
  const end = parseTimedWallClock(payload.end)
  if (!start) return null
  return {
    startDate: start,
    // Timed events must have a strictly-positive duration: an end equal to the
    // start expands to +1h, matching draftToPayload — so the draft never shows a
    // 0-duration range that the saved payload silently lengthens.
    endDate: end && end > start ? end : new Date(start.getTime() + 60 * 60 * 1000),
    allDay: false,
    timeZone: payload.timeZone,
  }
}

/**
 * Serialize an editing draft to the wire payload.
 * - Timed: local RFC3339 strings; enforces end >= start by preserving the draft
 *   end only when valid, else start + 1h.
 * - All-day: YYYY-MM-DD with an EXCLUSIVE end (inclusive display end + 1 day),
 *   enforcing end > start.
 */
export function draftToPayload(draft: WhenDraft): WhenPayload {
  if (draft.allDay) {
    const startStr = toDateString(draft.startDate)
    let inclusiveEnd = draft.endDate < draft.startDate ? draft.startDate : draft.endDate
    const inclusiveEndStr = toDateString(inclusiveEnd)
    const exclusiveEndStr = addDays(inclusiveEndStr, 1)
    // All-day payloads omit `timeZone` per the WhenPayload contract (see top of
    // file) — even when the draft still carries one from a timed→all-day switch.
    return {
      start: startStr,
      end: exclusiveEndStr,
      allDay: true,
    }
  }
  const start = draft.startDate
  const end = draft.endDate > start ? draft.endDate : new Date(start.getTime() + 60 * 60 * 1000)
  // Anchor the wall-clock to the event's zone when present (so changing the zone
  // re-anchors the instant); otherwise fall back to the browser's local offset.
  const serialize = (d: Date) => (draft.timeZone ? wallClockToZonedRfc3339(d, draft.timeZone) : toLocalRfc3339(d))
  return {
    start: serialize(start),
    end: serialize(end),
    allDay: false,
    ...(draft.timeZone ? { timeZone: draft.timeZone } : {}),
  }
}
